import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import {
  Brain,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  CheckCircle,
  Zap,
  Clock,
  Target,
  DollarSign,
  Activity,
  Shield,
  Lightbulb,
  BarChart3,
  RefreshCw,
} from 'lucide-react';

interface AIPredictionWidgetProps {
  title: string;
  prediction: string;
  confidence: number;
  trend: 'up' | 'down' | 'stable';
  timeframe: string;
}

export function AIPredictionWidget({ title, prediction, confidence, trend, timeframe }: AIPredictionWidgetProps) {
  const TrendIcon = trend === 'up' ? TrendingUp : trend === 'down' ? TrendingDown : Activity;
  const trendColor = trend === 'up' ? 'text-green-500' : trend === 'down' ? 'text-red-500' : 'text-blue-500';

  return (
    <Card className="relative overflow-hidden">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Brain className="h-4 w-4 text-purple-500" />
            {title}
          </CardTitle>
          <Badge variant="outline" className="text-xs">
            {confidence}% confident
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center gap-2">
          <TrendIcon className={`h-4 w-4 ${trendColor}`} />
          <span className="text-sm font-medium">{prediction}</span>
        </div>
        <div className="space-y-2">
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>Confidence Level</span>
            <span>{confidence}%</span>
          </div>
          <Progress value={confidence} className="h-2" />
        </div>
        <div className="text-xs text-muted-foreground">Timeframe: {timeframe}</div>
      </CardContent>
      <div className="absolute top-2 right-2 w-2 h-2 rounded-full bg-purple-500 animate-pulse" />
    </Card>
  );
}

interface AIRecommendationWidgetProps {
  title: string;
  recommendations: Array<{
    action: string;
    impact: string;
    effort: 'low' | 'medium' | 'high';
    savings?: string;
  }>;
}

export function AIRecommendationWidget({ title, recommendations }: AIRecommendationWidgetProps) {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  const effortColors = {
    low: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
    medium: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
    high: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Lightbulb className="h-4 w-4 text-yellow-500" />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {recommendations.map((rec, index) => (
          <div
            key={index}
            className="border rounded-lg p-3 cursor-pointer hover:bg-muted/50 transition-colors"
            onClick={() => setExpandedIndex(expandedIndex === index ? null : index)}
          >
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-sm font-medium">{rec.action}</p>
                <p className="text-xs text-muted-foreground mt-1">{rec.impact}</p>
              </div>
              <div className="flex items-center gap-2">
                {rec.savings && (
                  <Badge variant="outline" className="text-green-600 text-xs">
                    <DollarSign className="h-3 w-3 mr-1" />
                    {rec.savings}
                  </Badge>
                )}
                <Badge className={`text-xs ${effortColors[rec.effort]}`}>{rec.effort}</Badge>
              </div>
            </div>
            {expandedIndex === index && (
              <div className="mt-3 pt-3 border-t">
                <div className="flex gap-2">
                  <Button size="sm" className="flex-1">
                    Implement
                  </Button>
                  <Button size="sm" variant="outline" className="flex-1">
                    Learn More
                  </Button>
                </div>
              </div>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

interface AIMetricsWidgetProps {
  title: string;
  metrics: Array<{
    label: string;
    value: string | number;
    change?: number;
    icon: React.ComponentType<{ className?: string }>;
    color: string;
  }>;
}

export function AIMetricsWidget({ title, metrics }: AIMetricsWidgetProps) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Target className="h-4 w-4 text-blue-500" />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {metrics.map((metric, index) => {
          const Icon = metric.icon;
          return (
            <div key={index} className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${metric.color}`}>
                  <Icon className="h-4 w-4 text-white" />
                </div>
                <div>
                  <p className="text-sm font-medium">{metric.label}</p>
                  {metric.change !== undefined && (
                    <p className={`text-xs ${metric.change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {metric.change >= 0 ? '+' : ''}
                      {metric.change}% from last period
                    </p>
                  )}
                </div>
              </div>
              <div className="text-right">
                <p className="text-lg font-bold">{metric.value}</p>
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}

interface AIStatusWidgetProps {
  systemHealth: number;
  aiStatus: 'active' | 'training' | 'offline';
  lastUpdate: Date;
  activeTasks: string[];
}

export function AIStatusWidget({ systemHealth, aiStatus, lastUpdate, activeTasks }: AIStatusWidgetProps) {
  const statusConfig = {
    active: { color: 'bg-green-500', text: 'Active', icon: CheckCircle },
    training: { color: 'bg-yellow-500', text: 'Training', icon: Clock },
    offline: { color: 'bg-red-500', text: 'Offline', icon: AlertTriangle },
  };

  const config = statusConfig[aiStatus];
  const StatusIcon = config.icon;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Shield className="h-4 w-4 text-blue-500" />
          AI System Status
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">System Health</span>
          <div className="flex items-center gap-2">
            <Progress value={systemHealth} className="w-16 h-2" />
            <span className="text-sm font-medium">{systemHealth}%</span>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">AI Status</span>
          <div className="flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full ${config.color}`} />
            <span className="text-sm font-medium">{config.text}</span>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Last Update</span>
          <span className="text-sm font-medium">{lastUpdate.toLocaleTimeString()}</span>
        </div>

        {activeTasks.length > 0 && (
          <div className="space-y-2">
            <span className="text-sm text-muted-foreground">Active Tasks</span>
            <div className="space-y-1">
              {activeTasks.slice(0, 3).map((task, index) => (
                <div key={index} className="flex items-center gap-2 text-xs">
                  <div className="w-1 h-1 rounded-full bg-blue-500" />
                  <span>{task}</span>
                </div>
              ))}
              {activeTasks.length > 3 && (
                <p className="text-xs text-muted-foreground">+{activeTasks.length - 3} more tasks</p>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
